import {
  Add20Regular,
  Filter20Regular,
  ArrowDownload20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiltrarOrcamentosModal,
  type FiltrosOrcamentos,
} from "../../components/modals/FiltrarOrcamentosModal";
import { ExportarOrcamentosModal } from "../../components/modals/ExportarOrcamentosModal";
import {
  ApiError,
  formatBudgetStatusLabel,
  getBudgets,
  normalizeBudgetStatus,
  type BudgetStatus,
} from "../../orcamentos/orcamentosApi";
import { APP_CURRENCY, APP_LOCALE } from "../../config/locale";

interface OrcamentoView {
  id: number;
  ref: string;
  obra: string;
  cliente: string;
  data: string;
  createdAt: string;
  valor?: number | null;
  estado: BudgetStatus;
}

const statusOrder: BudgetStatus[] = [
  "elaboracao",
  "aguardando",
  "pronto",
  "enviado",
  "aceite",
  "recusado",
];

const formatCurrency = (value?: number | null): string => {
  if (value == null) return "—";

  const formatted = new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return formatted.replace(/\./g, " ");
};

const formatDate = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar orçamentos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os orçamentos.";
};

export function OrcamentosView() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState<
    BudgetStatus | "Todos"
  >("Todos");
  const [orcamentos, setOrcamentos] = useState<OrcamentoView[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [isFilterModalOpen, setIsFilterModalOpen] =
    useState(false);
  const [isExportModalOpen, setIsExportModalOpen] =
    useState(false);
  const [filtrosAtivos, setFiltrosAtivos] =
    useState<FiltrosOrcamentos | null>(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [viewMode, setViewMode] = useState<"list" | "cards">(
    "list",
  );
  const itensPorPagina = 15;

  useEffect(() => {
    let isMounted = true;

    const loadBudgets = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getBudgets();
        if (!isMounted) return;

        const mapped = response.map((budget) => ({
          id: budget.id,
          ref: budget.reference,
          obra: budget.workName,
          cliente: budget.clientName,
          data: formatDate(budget.created),
          createdAt: budget.created,
          valor: budget.totalAmount ?? null,
          estado: normalizeBudgetStatus(budget.status),
        }));

        setOrcamentos(mapped);
      } catch (error) {
        if (!isMounted) return;
        setOrcamentos([]);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadBudgets();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAplicarFiltros = (filtros: FiltrosOrcamentos) => {
    setFiltrosAtivos(filtros);
    setPaginaAtual(1);
  };

  // Verifica se há algum filtro ativo
  const hasFiltrosAtivos =
    filtrosAtivos &&
    (filtrosAtivos.estados.length > 0 ||
      filtrosAtivos.cliente !== "" ||
      filtrosAtivos.obra !== "" ||
      filtrosAtivos.dataCriacaoDe !== "" ||
      filtrosAtivos.dataCriacaoAte !== "" ||
      filtrosAtivos.valorMinimo !== "" ||
      filtrosAtivos.valorMaximo !== "" ||
      filtrosAtivos.prioridade !== "");

  const actions = (
    <>
      <div
        className="hidden sm:flex items-center bg-card border border-border rounded"
        style={{ borderRadius: "var(--radius-button)" }}
      >
        <button
          onClick={() => setViewMode("cards")}
          className={`px-3 py-2 rounded transition-colors min-h-[44px] ${
            viewMode === "cards"
              ? "bg-accent text-accent-foreground"
              : "text-secondary-text hover:text-foreground"
          }`}
          style={{ borderRadius: "var(--radius-button)" }}
        >
          Cards
        </button>
        <button
          onClick={() => setViewMode("list")}
          className={`px-3 py-2 rounded transition-colors min-h-[44px] ${
            viewMode === "list"
              ? "bg-accent text-accent-foreground"
              : "text-secondary-text hover:text-foreground"
          }`}
          style={{ borderRadius: "var(--radius-button)" }}
        >
          Lista
        </button>
      </div>
      <button
        onClick={() => setIsFilterModalOpen(true)}
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px] relative"
        style={{ borderRadius: "var(--radius-button)" }}
      >
        <Filter20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Filtrar</span>
        {hasFiltrosAtivos && (
          <span
            className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full border-2 border-background"
            style={{ borderRadius: "50%" }}
          />
        )}
      </button>
      <button
        onClick={() => setIsExportModalOpen(true)}
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
      >
        <ArrowDownload20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Exportar</span>
      </button>
      <button
        onClick={() => navigate("/orcamentos/novo")}
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Orçamento
      </button>
    </>
  );

  const filteredData = useMemo(() => {
    let result = [...orcamentos];

    if (selectedFilter !== "Todos") {
      result = result.filter(
        (item) => item.estado === selectedFilter,
      );
    }

    if (filtrosAtivos) {
      if (filtrosAtivos.estados.length > 0) {
        result = result.filter((item) =>
          filtrosAtivos.estados.includes(item.estado),
        );
      }

      if (filtrosAtivos.cliente) {
        const clienteFiltro =
          filtrosAtivos.cliente.toLowerCase();
        result = result.filter(
          (item) =>
            item.cliente.toLowerCase() === clienteFiltro,
        );
      }

      if (filtrosAtivos.obra) {
        const obraFiltro = filtrosAtivos.obra.toLowerCase();
        result = result.filter(
          (item) => item.obra.toLowerCase() === obraFiltro,
        );
      }

      if (filtrosAtivos.dataCriacaoDe) {
        const startDate = new Date(filtrosAtivos.dataCriacaoDe);
        if (!Number.isNaN(startDate.getTime())) {
          result = result.filter(
            (item) => new Date(item.createdAt) >= startDate,
          );
        }
      }

      if (filtrosAtivos.dataCriacaoAte) {
        const endDate = new Date(filtrosAtivos.dataCriacaoAte);
        if (!Number.isNaN(endDate.getTime())) {
          endDate.setHours(23, 59, 59, 999);
          result = result.filter(
            (item) => new Date(item.createdAt) <= endDate,
          );
        }
      }

      if (filtrosAtivos.valorMinimo) {
        const minValue = Number.parseFloat(
          filtrosAtivos.valorMinimo,
        );
        if (!Number.isNaN(minValue)) {
          result = result.filter(
            (item) => item.valor != null && item.valor >= minValue,
          );
        }
      }

      if (filtrosAtivos.valorMaximo) {
        const maxValue = Number.parseFloat(
          filtrosAtivos.valorMaximo,
        );
        if (!Number.isNaN(maxValue)) {
          result = result.filter(
            (item) => item.valor != null && item.valor <= maxValue,
          );
        }
      }
    }

    return result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() -
        new Date(a.createdAt).getTime(),
    );
  }, [orcamentos, selectedFilter, filtrosAtivos]);

  const clientesDisponiveis = useMemo(() => {
    return Array.from(
      new Set(
        orcamentos
          .map((item) => item.cliente)
          .filter((cliente) => cliente.trim() !== ""),
      ),
    ).sort((a, b) => a.localeCompare(b, APP_LOCALE));
  }, [orcamentos]);

  const obrasDisponiveis = useMemo(() => {
    return Array.from(
      new Set(
        orcamentos
          .map((item) => item.obra)
          .filter((obra) => obra.trim() !== ""),
      ),
    ).sort((a, b) => a.localeCompare(b, APP_LOCALE));
  }, [orcamentos]);

  const filterOptions: Array<BudgetStatus | "Todos"> = [
    "Todos",
    ...statusOrder,
  ];

  const getEstadoBadgeClass = (estado: BudgetStatus) => {
    switch (estado) {
      case "elaboracao":
        return "bg-chart-2/10 text-chart-2";
      case "aguardando":
        return "bg-chart-4/10 text-chart-4";
      case "pronto":
        return "bg-chart-3/10 text-chart-3";
      case "enviado":
        return "bg-chart-1/10 text-chart-1";
      case "aceite":
        return "bg-primary/10 text-primary";
      case "recusado":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getEstadoRowClass = (estado: BudgetStatus) => {
    switch (estado) {
      case "elaboracao":
        return "bg-chart-2/10";
      case "aguardando":
        return "bg-chart-4/10";
      case "pronto":
        return "bg-chart-3/10";
      case "enviado":
        return "bg-chart-1/10";
      default:
        return "";
    }
  };

  const data = filteredData;

  const startIndex = (paginaAtual - 1) * itensPorPagina;
  const endIndex = startIndex + itensPorPagina;
  const dataPaginada = data.slice(startIndex, endIndex);
  const totalPaginas = Math.ceil(data.length / itensPorPagina);

  const renderListView = () => (
    <>
      {/* Desktop Table */}
      <div
        className="hidden md:block bg-card border border-border overflow-hidden"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight:
                    "var(--font-weight-semi-bold)",
                }}
              >
                Ref.
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight:
                    "var(--font-weight-semi-bold)",
                }}
              >
                Obra
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight:
                    "var(--font-weight-semi-bold)",
                }}
              >
                Cliente
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight:
                    "var(--font-weight-semi-bold)",
                }}
              >
                Data de Criação
              </th>
              <th
                className="px-6 py-3 text-left"
                style={{
                  fontWeight:
                    "var(--font-weight-semi-bold)",
                }}
              >
                Estado
              </th>
            </tr>
          </thead>
          <tbody>
            {dataPaginada.map((item) => (
              <tr
                key={item.id}
                className={`border-b border-border last:border-b-0 hover:bg-accent/30 transition-colors cursor-pointer ${getEstadoRowClass(item.estado)}`}
                onClick={() =>
                  navigate(`/orcamentos/editar/${item.id}`)
                }
              >
                <td
                  className="px-6 py-4"
                  style={{
                    fontWeight:
                      "var(--font-weight-semi-bold)",
                  }}
                >
                  {item.ref}
                </td>
                <td className="px-6 py-4 text-secondary-text">
                  {item.obra}
                </td>
                <td className="px-6 py-4">
                  {item.cliente}
                </td>
                <td className="px-6 py-4 text-secondary-text">
                  {item.data}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex px-2 py-1 rounded bg-muted ${getEstadoBadgeClass(item.estado)}`}
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight:
                        "var(--font-weight-semi-bold)",
                      borderRadius: "var(--radius)",
                    }}
                  >
                    {formatBudgetStatusLabel(item.estado)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden space-y-3">
        {dataPaginada.map((item) => (
          <div
            key={item.id}
            className="bg-card border border-border p-4 space-y-3 cursor-pointer hover:border-primary/50 transition-colors"
            style={{ borderRadius: "var(--radius-card)" }}
            onClick={() =>
              navigate(`/orcamentos/editar/${item.id}`)
            }
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p
                  style={{
                    fontWeight:
                      "var(--font-weight-semi-bold)",
                  }}
                >
                  {item.ref}
                </p>
                <span
                  className={`inline-flex px-2 py-1 rounded mt-1 ${getEstadoBadgeClass(item.estado)}`}
                  style={{
                    fontSize: "var(--text-caption)",
                    borderRadius: "var(--radius)",
                  }}
                >
                  {formatBudgetStatusLabel(item.estado)}
                </span>
              </div>
            </div>
            <div>
              <p
                className="text-secondary-text"
                style={{ fontSize: "var(--text-caption)" }}
              >
                Cliente
              </p>
              <p className="mt-0.5">{item.cliente}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p
                  className="text-secondary-text"
                  style={{
                    fontSize: "var(--text-caption)",
                  }}
                >
                  Obra
                </p>
                <p className="mt-0.5">{item.obra}</p>
              </div>
              <div>
                <p
                  className="text-secondary-text"
                  style={{
                    fontSize: "var(--text-caption)",
                  }}
                >
                  Data
                </p>
                <p className="mt-0.5">{item.data}</p>
              </div>
            </div>
            <div className="pt-3 border-t border-border">
              <p
                className="text-secondary-text"
                style={{ fontSize: "var(--text-caption)" }}
              >
                Valor Total
              </p>
              <p
                className="mt-0.5"
                style={{
                  fontWeight:
                    "var(--font-weight-semi-bold)",
                }}
              >
                {formatCurrency(item.valor)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {dataPaginada.map((item) => (
        <div
          key={item.id}
          className="bg-card border border-border p-4 space-y-3 cursor-pointer hover:border-primary/50 transition-colors"
          style={{ borderRadius: "var(--radius-card)" }}
          onClick={() =>
            navigate(`/orcamentos/editar/${item.id}`)
          }
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p
                style={{
                  fontWeight:
                    "var(--font-weight-semi-bold)",
                }}
              >
                {item.ref}
              </p>
              <span
                className={`inline-flex px-2 py-1 rounded mt-1 ${getEstadoBadgeClass(item.estado)}`}
                style={{
                  fontSize: "var(--text-caption)",
                  borderRadius: "var(--radius)",
                }}
              >
                {formatBudgetStatusLabel(item.estado)}
              </span>
            </div>
          </div>
          <div>
            <p
              className="text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Cliente
            </p>
            <p className="mt-0.5">{item.cliente}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p
                className="text-secondary-text"
                style={{
                  fontSize: "var(--text-caption)",
                }}
              >
                Obra
              </p>
              <p className="mt-0.5">{item.obra}</p>
            </div>
            <div>
              <p
                className="text-secondary-text"
                style={{
                  fontSize: "var(--text-caption)",
                }}
              >
                Data
              </p>
              <p className="mt-0.5">{item.data}</p>
            </div>
          </div>
          <div className="pt-3 border-t border-border">
            <p
              className="text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Valor Total
            </p>
            <p
              className="mt-0.5"
              style={{
                fontWeight:
                  "var(--font-weight-semi-bold)",
              }}
            >
              {formatCurrency(item.valor)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <PageHeader
        title="Orçamentos"
        subtitle="Orçamentos em elaboração, enviados e adjudicados"
        actions={actions}
      />

      {/* Content */}
      <div
        className="flex-1 overflow-auto px-4 md:px-6"
        style={{
          paddingTop: "var(--spacing-page-header-bottom)",
          paddingBottom: "var(--spacing-page-header-bottom)",
        }}
      >
        <div className="space-y-4">
          {/* Status Filter Chips */}
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => {
              const optionLabel =
                option === "Todos"
                  ? "Todos"
                  : formatBudgetStatusLabel(option);

              return (
                <button
                  key={option}
                  onClick={() => {
                    setSelectedFilter(option);
                    setPaginaAtual(1);
                  }}
                  className={`px-3 py-1.5 rounded transition-colors ${
                    selectedFilter === option
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary hover:bg-accent border border-border text-foreground"
                  }`}
                  style={{
                    borderRadius: "var(--radius)",
                    fontSize: "var(--text-caption)",
                    fontWeight:
                      selectedFilter === option
                        ? "var(--font-weight-semi-bold)"
                        : "var(--font-weight-normal)",
                  }}
                >
                  {optionLabel}
                </button>
              );
            })}
          </div>

          {loadError && (
            <div
              className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
              style={{ borderRadius: "var(--radius)" }}
            >
              {loadError}
            </div>
          )}

          {isLoading && (
            <div
              className="border border-border bg-card px-4 py-3 text-secondary-text"
              style={{ borderRadius: "var(--radius)" }}
            >
              A carregar orçamentos...
            </div>
          )}

          {!isLoading && !loadError && data.length === 0 && (
            <div
              className="border border-border bg-card px-4 py-6 text-secondary-text text-center"
              style={{ borderRadius: "var(--radius)" }}
            >
              Sem orçamentos para apresentar.
            </div>
          )}

          {!isLoading && data.length > 0 && (
            <>
              {viewMode === "cards"
                ? renderCardView()
                : renderListView()}

              {/* Pagination */}
              {totalPaginas > 1 && (
                <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                  {/* Info de resultados */}
                  <div
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-base)" }}
                  >
                    A mostrar {startIndex + 1} -{" "}
                    {Math.min(endIndex, data.length)} de{" "}
                    {data.length} orçamentos
                  </div>

                  {/* Controles de paginação */}
                  <div className="flex items-center gap-2">
                    {/* Botão Anterior */}
                    <button
                      onClick={() =>
                        setPaginaAtual((prev) =>
                          Math.max(1, prev - 1),
                        )
                      }
                      disabled={paginaAtual === 1}
                      className="p-2 border border-border rounded hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      style={{ borderRadius: "var(--radius)" }}
                      aria-label="Página anterior"
                    >
                      <ChevronLeft20Regular className="w-5 h-5" />
                    </button>

                    {/* Números de página */}
                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: totalPaginas },
                        (_, i) => i + 1,
                      ).map((pagina) => {
                        const mostrarPagina =
                          pagina === 1 ||
                          pagina === totalPaginas ||
                          (pagina >= paginaAtual - 1 &&
                            pagina <= paginaAtual + 1);

                        const mostrarReticencias =
                          (pagina === paginaAtual - 2 &&
                            paginaAtual > 3) ||
                          (pagina === paginaAtual + 2 &&
                            paginaAtual < totalPaginas - 2);

                        if (mostrarReticencias) {
                          return (
                            <span
                              key={pagina}
                              className="px-2 text-secondary-text"
                              style={{
                                fontSize: "var(--text-base)",
                              }}
                            >
                              ...
                            </span>
                          );
                        }

                        if (!mostrarPagina) return null;

                        return (
                          <button
                            key={pagina}
                            onClick={() => setPaginaAtual(pagina)}
                            className={`min-w-[40px] h-10 px-3 border rounded transition-colors ${
                              pagina === paginaAtual
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-border hover:bg-accent"
                            }`}
                            style={{
                              borderRadius: "var(--radius)",
                              fontSize: "var(--text-base)",
                            }}
                          >
                            {pagina}
                          </button>
                        );
                      })}
                    </div>

                    {/* Botão Próximo */}
                    <button
                      onClick={() =>
                        setPaginaAtual((prev) =>
                          Math.min(totalPaginas, prev + 1),
                        )
                      }
                      disabled={paginaAtual === totalPaginas}
                      className="p-2 border border-border rounded hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      style={{ borderRadius: "var(--radius)" }}
                      aria-label="Próxima página"
                    >
                      <ChevronRight20Regular className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de Filtros */}
      <FiltrarOrcamentosModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onAplicarFiltros={handleAplicarFiltros}
        clientesDisponiveis={clientesDisponiveis}
        obrasDisponiveis={obrasDisponiveis}
      />

      {/* Modal de Exportação */}
      <ExportarOrcamentosModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        totalOrcamentosFiltrados={data.length}
      />
    </div>
  );
}
